import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import close from "../../assets/icons/close.svg";
import { Postdata } from "../../API/GlobalApi";
import { formatDateDDMMYYYYHHMMSS_UTC } from "../../Utils/data&dateTime";
import { copyToClipboard } from "../../Utils/copyToClipboard";
import "../../assets/css/Modal.css";
import "../../assets/css/StudentShortDetails.css";

/**
 * Ledger details for a non-student (CRM Student) user. Pulls the full ledger
 * from /api/v1/students/get-user-profile/ — same payload the Receive Payment
 * modal already uses — and renders the Date/Particular/Debit/Credit/Balance
 * table so a CRM Student's history looks the same here as inside Receive
 * Payment.
 */
export const OtherLedgerDetails = ({ isOpen, onClose, selectedRow }) => {
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState(null);

    const mobile = selectedRow?.user_mobile || "";

    useEffect(() => {
        if (!isOpen || !mobile) return;
        let cancelled = false;
        setLoading(true);
        setProfile(null);
        (async () => {
            try {
                const res = await Postdata("students/get-user-profile/", { mobile });
                if (!cancelled) setProfile(res?.data || null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, mobile]);

    const ledgerEntries = profile?.finance_data?.ledger_entries || [];

    const unifiedEntries = useMemo(() => {
        return ledgerEntries
            .map((e) => ({
                ...e,
                entryType: e.payment_type === "Debit" ? "Debit" : "Credit",
                sortDate:
                    e.payment_type === "Credit" && e.payment_date
                        ? e.payment_date
                        : e.created_at,
            }))
            .sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate));
    }, [ledgerEntries]);

    const { totalDebit, totalCredit, finalBalance } = useMemo(() => {
        let d = 0;
        let c = 0;
        let running = 0;
        unifiedEntries.forEach((item) => {
            const amt = Number(item.amount) || 0;
            if (item.entryType === "Debit") {
                d += amt;
                running -= amt;
            } else {
                c += amt;
                running += amt;
            }
        });
        return { totalDebit: d, totalCredit: c, finalBalance: running };
    }, [unifiedEntries]);

    if (!isOpen) return null;

    const name = profile?.full_name || selectedRow?.user_name || "-";
    const displayMobile = profile?.mobile || mobile || "-";
    const profileType = profile?.profile || "-";
    const courseLabel = profile?.career_arc_inquiry?.course;

    return createPortal(
        <div className="modal-overlay" style={{ zIndex: 99999999 }}>
            <div
                className="modal-content studentLedgerReport studentledgerIndividual"
                style={{
                    position: "relative",
                    width: "70%",
                    display: "flex",
                    flexDirection: "column",
                    height: "84vh",
                    padding: 0,
                    overflow: "hidden",
                }}
            >
                <div
                    className="modal-header-content"
                    style={{
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 20px",
                        background: "#fff",
                    }}
                >
                    <h2
                        className="text-primary"
                        style={{
                            background: "#F9F9F9",
                            padding: "16px",
                            borderRadius: "8px",
                            textAlign: "left",
                            fontWeight: 500,
                            fontSize: "18px",
                            color: "#222F3E",
                            display: "flex",
                            gap: "5px",
                            width: "100%",
                        }}
                    >
                        {name.toUpperCase()}
                        <span style={{ margin: "0 10px" }}>|</span>
                        <span>{profileType}</span>
                        {courseLabel && (
                            <>
                                <span style={{ margin: "0 10px" }}>|</span>
                                <span>{courseLabel}</span>
                            </>
                        )}
                        <span style={{ margin: "0 10px" }}>|</span>
                        <span
                            style={{ cursor: "pointer" }}
                            title="Copy Contact Number"
                            onClick={() => copyToClipboard(displayMobile, "Contact copied")}
                        >
                            {displayMobile}
                        </span>
                    </h2>
                    <button className="close-btn" onClick={onClose}>
                        <img src={close} alt="close" />
                    </button>
                </div>

                <div style={{ flex: 1, overflow: "auto", padding: "0 20px 20px" }}>
                    {loading ? (
                        <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>
                            Loading…
                        </div>
                    ) : (
                        <div
                            className="mt-2 paymentreporttablecontainer ledgerWrapper"
                            style={{
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                            }}
                        >
                            <table className="table table-sm table-bordered table-hover student-table fee ledgerTable">
                                <thead className="ledgerHeader">
                                    <tr>
                                        <th style={{ padding: "10px" }}>Date</th>
                                        <th style={{ padding: "10px" }}>Particular</th>
                                        <th style={{ padding: "10px", textAlign: "right" }}>Debit</th>
                                        <th style={{ padding: "10px", textAlign: "right" }}>Credit</th>
                                        <th style={{ padding: "10px", textAlign: "right" }}>Balance</th>
                                    </tr>
                                </thead>
                                <tbody className="ledgerBody">
                                    {unifiedEntries.length > 0 ? (
                                        (() => {
                                            let runningBalance = 0;
                                            return unifiedEntries.map((item, idx) => {
                                                const amt = Number(item.amount) || 0;
                                                if (item.entryType === "Credit") runningBalance += amt;
                                                else runningBalance -= amt;
                                                return (
                                                    <tr key={idx}>
                                                        <td style={{ padding: "10px" }}>
                                                            {item.entryType === "Credit" && item.payment_date
                                                                ? formatDateDDMMYYYYHHMMSS_UTC(item.payment_date)
                                                                : formatDateDDMMYYYYHHMMSS_UTC(item.created_at)}
                                                        </td>
                                                        <td style={{ padding: "10px" }}>
                                                            {item.entryType === "Debit"
                                                                ? item.fee_head?.name || "-"
                                                                : (
                                                                    <>
                                                                        {item.mode_of_payment}
                                                                        {item.transaction?.cheque_number && (
                                                                            <div>
                                                                                <strong>Cheque:</strong>{" "}
                                                                                {item.transaction.cheque_number}
                                                                            </div>
                                                                        )}
                                                                        {item.transaction?.dd_number && (
                                                                            <div>
                                                                                <strong>DD:</strong> {item.transaction.dd_number}
                                                                            </div>
                                                                        )}
                                                                        {item.transaction?.utr_number && (
                                                                            <div>
                                                                                <strong>UTR:</strong> {item.transaction.utr_number}
                                                                            </div>
                                                                        )}
                                                                        {item.receipt_number && (
                                                                            <div style={{ color: "#888", fontSize: "12px" }}>
                                                                                {item.receipt_number}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                        </td>
                                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                                            ₹{" "}
                                                            {item.entryType === "Debit"
                                                                ? amt.toLocaleString("en-IN", {
                                                                    minimumFractionDigits: 2,
                                                                })
                                                                : "0.00"}
                                                        </td>
                                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                                            ₹{" "}
                                                            {item.entryType === "Credit"
                                                                ? amt.toLocaleString("en-IN", {
                                                                    minimumFractionDigits: 2,
                                                                })
                                                                : "0.00"}
                                                        </td>
                                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                                            ₹{" "}
                                                            {runningBalance.toLocaleString("en-IN", {
                                                                minimumFractionDigits: 2,
                                                            })}
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center" style={{ padding: "20px" }}>
                                                No Ledger Entries Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {unifiedEntries.length > 0 && (
                                    <tfoot className="ledgerFooter">
                                        <tr>
                                            <td colSpan="2" style={{ padding: "10px", fontWeight: 600 }}>
                                                Total
                                            </td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: 600 }}>
                                                ₹ {totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: 600 }}>
                                                ₹ {totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: 600 }}>
                                                ₹ {finalBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default OtherLedgerDetails;
